/**
 * Typed fetch wrapper — the client's ONLY transport layer.
 *
 * Two error idioms, chosen per read-site (the incumbent's R13 lesson):
 *  - `getOr` swallows failures to a fallback for harmless list reads where an
 *    empty render is honest (e.g. venue suggestions).
 *  - `getJson`/`postJson` THROW on !res.ok where silent-empty would mislead
 *    the operator (reconcile reads, registry load, quote/persist writes).
 *    Callers surface friendly copy from their own maps (SEC-5: a raw STATUS
 *    CODE never reaches the screen, and neither does a server body unless the
 *    read-site has argued for that one status in place).
 *
 * ⚠️ SEC-5 IS A RULE ABOUT THE DEFAULT, NOT AN ABSOLUTE. There are TWO
 * deliberate exceptions in the client, and this comment said "exactly one"
 * until T1868 M4 added the second without updating it:
 *  1. lib/actions.ts's `adminFailure` renders `body.error` verbatim for a 403
 *     from the operator-admin routes, because the two refusals it can carry
 *     ("you cannot disable your own account", "that is the last account with
 *     access") ARE the actionable sentences and an invented generic would hide
 *     which rule was hit.
 *  2. lib/actions.ts's `savePortalHomeSettings` renders `body.error` verbatim
 *     for a 400 from the per-home settings write (T1868 M4). The argument is
 *     made at that read-site: the generic 400 copy names the name and the email
 *     address, fields that block does not have, and it was MEASURED telling
 *     Helen nothing on the live seat. ⚠️ Its body is composed UPSTREAM by the
 *     portal, not by this service, so unlike (1) it is bounded before it is
 *     admitted — `firstNamedPortalError` / `MAX_FORWARDED_PORTAL_DETAIL` in
 *     server/app.ts. A third exception over an upstream body needs the same
 *     bound, not just the same argument.
 * Adding an exception means arguing it at the read-site the same way, in a
 * comment, over a status whose bodies are a KNOWN closed set — never widening
 * this transport to pass bodies through by default.
 */

export class ApiError extends Error {
  readonly status: number;
  /** Parsed response body when the server sent JSON; null otherwise. Used to KEY
   *  friendly-copy maps. Rendering it raw is the exception, not the idiom, and
   *  needs the argument the header describes — TWO read-sites have made it
   *  (SEC-5): lib/actions.ts's `adminFailure` 403 arm and, since T1868 M4, its
   *  `savePortalHomeSettings` 400 arm. This said "the only place" while the
   *  second already existed. */
  readonly body: unknown;

  constructor(status: number, body: unknown) {
    super(`API request failed with status ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

async function parseBody(res: Response): Promise<unknown> {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

/** GET returning parsed JSON; throws ApiError on any non-2xx. */
export async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(path, { credentials: 'same-origin' });
  if (!res.ok) throw new ApiError(res.status, await parseBody(res));
  return (await res.json()) as T;
}

/**
 * GET returning the raw body as a Blob; throws ApiError on any non-2xx, with the
 * JSON refusal body parsed for the caller to KEY copy on (board 2764 M6: Stripe's
 * account invoice PDF, streamed by the kiosk, whose 409 `not_ready` the Invoices
 * tab words rather than downloading an error body).
 */
export async function getBlob(path: string): Promise<Blob> {
  const res = await fetch(path, { credentials: 'same-origin' });
  if (!res.ok) throw new ApiError(res.status, await parseBody(res));
  return res.blob();
}

/** GET for harmless list reads: any failure resolves to `fallback`. Use ONLY
 *  where an empty render is honest — never for reconcile/badge-truth reads. */
export async function getOr<T>(path: string, fallback: T): Promise<T> {
  try {
    return await getJson<T>(path);
  } catch {
    return fallback;
  }
}

/**
 * DELETE returning parsed JSON; throws ApiError on any non-2xx.
 *
 * ⚠️ THE FIRST DELETE IN THIS CLIENT (T1141 M5), added rather than faked with a
 * POST. `DELETE /api/push/subscribe` takes its id in the QUERY STRING because
 * `server/http.ts` parses a body for POST/PUT/PATCH only — a DELETE arrives at
 * the handler with `body: null`, always — so there is no body to send and none
 * is sent.
 *
 * ⚠️ NO `content-type` HEADER, DELIBERATELY, and not merely because there is no
 * body: the route's own comment records that it runs no `requiresJsonBody`
 * check, so a content type here would be a header the server never reads.
 * The CSRF property is unchanged and rests on two independent mechanisms named
 * at that handler: an HTML form can emit GET and POST and nothing else, and a
 * cross-origin `fetch` with method DELETE is preflighted against a service that
 * answers with no CORS headers. `SameSite=Strict` sits under both.
 */
export async function deleteJson<T>(path: string): Promise<T> {
  const res = await fetch(path, { method: 'DELETE', credentials: 'same-origin' });
  if (!res.ok) throw new ApiError(res.status, await parseBody(res));
  return (await res.json()) as T;
}

/** POST JSON returning parsed JSON; throws ApiError on any non-2xx. */
export async function postJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new ApiError(res.status, await parseBody(res));
  return (await res.json()) as T;
}
